import { ArrowRight, ChevronDown, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { formatDateForInputLocal } from 'src/helpers/date.helpers';
import MultiSelector from 'src/pages/client/reports/components/multi-selector';
import DateRangeModal from 'src/pages/performance/components/date-range-modal';
import { DateFilterType } from 'src/pages/performance/tabs/overview-tab';
import { formatPlatformLabel } from 'src/pages/reports/utils/format-reporting-labels';
import {
  useGetReportingPortalAccountsQuery,
  useGetReportingPortalCampaignsQuery,
  useGetReportingPortalLineItemsQuery,
  useGetReportingPortalSourcesQuery,
} from 'src/redux/admin/reporting-api-v1/reporting-portal-filters.endpoints';
import { RootState } from 'src/redux/redux-store';

export interface ReportingApiV1FilterSelection {
  source: string;
  accounts: { id: string | number; name: string }[];
  campaigns: { id: string | number; name: string }[];
  lineItems: { id: string | number; name: string }[];
  dateFilter: string;
  startDate: string;
  endDate: string;
}

interface ReportingApiV1FilterBarProps {
  className?: string;
  selectedClassName?: string;
  onSelectionChange: (selection: ReportingApiV1FilterSelection) => void;
}

const sanitizeClientId = (value?: string | null): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  return trimmed;
};

const ReportingApiV1FilterBar: React.FC<ReportingApiV1FilterBarProps> = ({
  className = 'bg-gray-200 text-black rounded-full',
  selectedClassName = 'bg-blue-500 text-white',
  onSelectionChange,
}) => {
  const urlParams = new URLSearchParams(location.search);
  const userState = useSelector((state: RootState) => state.userState);
  const isClientView =
    userState.permissions?.groups?.includes('client_user') ||
    userState.permissions?.groups?.includes('client_admin');

  const previewClientId = sanitizeClientId(urlParams.get('previewClientId'));
  const activeClientId = sanitizeClientId(userState.activeClient?.ext_client_id);
  // Match legacy ReportsMenuBar: org users without a client use /v1/b2b/... (unscoped).
  // When preview/active client is set, use /v1/client/... + ext_client_id.
  const filterClientId = isClientView ? undefined : previewClientId || activeClientId;

  const [selectedPlatform, setSelectedPlatform] = useState<string>(urlParams.get('platform') || '');
  const [selectedAccounts, setSelectedAccounts] = useState<{ id: string | number; name: string }[]>(
    urlParams.get('accountIds')
      ? urlParams.get('accountIds')!.split(',').filter(Boolean).map((id) => ({ id, name: '' }))
      : [],
  );
  const [selectedCampaigns, setSelectedCampaigns] = useState<{ id: string | number; name: string }[]>(
    urlParams.get('campaignIds')
      ? urlParams.get('campaignIds')!.split(',').filter(Boolean).map((id) => ({ id, name: '' }))
      : [],
  );
  const [selectedLineItems, setSelectedLineItems] = useState<{ id: string | number; name: string }[]>(
    urlParams.get('lineItemIds')
      ? urlParams.get('lineItemIds')!.split(',').filter(Boolean).map((id) => ({ id, name: '' }))
      : [],
  );
  const [dateFilter, setDateFilter] = useState<DateFilterType>('' as DateFilterType);
  const [startDate, setStartDate] = useState<string>(urlParams.get('startDate') || '');
  const [endDate, setEndDate] = useState<string>(urlParams.get('endDate') || '');

  const { data: platforms = [], isFetching: isFetchingPlatforms } = useGetReportingPortalSourcesQuery({
    previewClientId: filterClientId,
  });

  const { data: accounts = [], isFetching: isFetchingAccounts } = useGetReportingPortalAccountsQuery(
    { source: selectedPlatform, previewClientId: filterClientId },
    { skip: !selectedPlatform },
  );

  const {
    data: campaigns = [],
    isFetching: isFetchingCampaigns,
    refetch: refetchCampaigns,
  } = useGetReportingPortalCampaignsQuery(
    {
      source: selectedPlatform,
      accountId: selectedAccounts.map((account) => account.id),
      previewClientId: filterClientId,
    },
    { skip: selectedAccounts.length === 0 },
  );

  const { data: lineItems = [], isFetching: isFetchingLineItems } =
    useGetReportingPortalLineItemsQuery(
      {
        source: selectedPlatform,
        accountId: selectedAccounts.map((account) => account.id),
        campaignId: selectedCampaigns.map((campaign) => campaign.id),
        previewClientId: filterClientId,
      },
      {
        skip: selectedAccounts.length === 0 || selectedCampaigns.length === 0,
      },
    );

  const selectedAccountIdsKey = selectedAccounts.map((item) => String(item.id)).join(',');
  const selectedCampaignIdsKey = selectedCampaigns.map((item) => String(item.id)).join(',');
  const selectedLineItemIdsKey = selectedLineItems.map((item) => String(item.id)).join(',');

  useEffect(() => {
    if (platforms.length > 0 && !selectedPlatform) {
      setSelectedPlatform(platforms[0]);
    }
  }, [platforms, selectedPlatform]);

  // Reset dependent selections when account *ids* change (not when names hydrate / first mount).
  const previousAccountIdsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (previousAccountIdsKeyRef.current === null) {
      previousAccountIdsKeyRef.current = selectedAccountIdsKey;
      if (selectedAccountIdsKey) {
        void refetchCampaigns();
      }
      return;
    }
    if (previousAccountIdsKeyRef.current === selectedAccountIdsKey) {
      return;
    }
    previousAccountIdsKeyRef.current = selectedAccountIdsKey;
    setSelectedCampaigns([]);
    setSelectedLineItems([]);
    if (selectedAccountIdsKey) {
      void refetchCampaigns();
    }
  }, [selectedAccountIdsKey, refetchCampaigns]);

  // Hydrate names for URL-seeded selections once lists load.
  useEffect(() => {
    if (!accounts.length || !selectedAccountIdsKey) return;
    setSelectedAccounts((prev) => {
      if (!prev.some((item) => !item.name)) return prev;
      return prev.map((item) => {
        const match = accounts.find((account) => String(account.id) === String(item.id));
        return match ? { id: match.id, name: match.name } : item;
      });
    });
  }, [accounts, selectedAccountIdsKey]);

  useEffect(() => {
    if (!campaigns.length || !selectedCampaignIdsKey) return;
    setSelectedCampaigns((prev) => {
      if (!prev.some((item) => !item.name)) return prev;
      return prev.map((item) => {
        const match = campaigns.find((campaign) => String(campaign.id) === String(item.id));
        return match ? { id: match.id, name: match.name } : item;
      });
    });
  }, [campaigns, selectedCampaignIdsKey]);

  useEffect(() => {
    if (!lineItems.length || !selectedLineItemIdsKey) return;
    setSelectedLineItems((prev) => {
      if (!prev.some((item) => !item.name)) return prev;
      return prev.map((item) => {
        const match = lineItems.find((lineItem) => String(lineItem.id) === String(item.id));
        return match ? { id: match.id, name: match.name } : item;
      });
    });
  }, [lineItems, selectedLineItemIdsKey]);

  const handlePlatformSelection = (platform: string) => {
    setSelectedPlatform(platform);
    setSelectedAccounts([]);
    setSelectedCampaigns([]);
    setSelectedLineItems([]);
  };

  const handleAccountSelection = (items: { id: string | number; name: string }[]) => {
    setSelectedAccounts(items);
    setSelectedCampaigns([]);
    setSelectedLineItems([]);
  };

  const handleCampaignSelection = (items: { id: string | number; name: string }[]) => {
    setSelectedCampaigns(items);
    setSelectedLineItems([]);
  };

  const handleLineItemSelection = (items: { id: string | number; name: string }[]) => {
    setSelectedLineItems(items);
  };

  const handleDateChange = (nextFilter: string, _startDate?: string, _endDate?: string) => {
    setDateFilter(nextFilter as DateFilterType);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    let newStartDate = '';
    let newEndDate = '';

    switch (nextFilter) {
      case 'last_month':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth - 1, 1));
        newEndDate = formatDateForInputLocal(new Date(currentYear, currentMonth, 0));
        break;
      case 'this_month':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'last_week':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 8));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'last_quarter':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth - 3, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'last_3_days':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 4));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'yesterday':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 2));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'year_to_date':
        newStartDate = formatDateForInputLocal(new Date(currentYear, 0, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;
      case 'custom':
        if (_startDate && _endDate) {
          newStartDate = _startDate;
          newEndDate = _endDate;
        }
        break;
      default:
        newStartDate =
          _startDate || formatDateForInputLocal(new Date(currentYear, currentMonth - 1, 1));
        newEndDate =
          _endDate || formatDateForInputLocal(new Date(currentYear, currentMonth, 0));
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleClear = () => {
    handlePlatformSelection(platforms[0] || '');
    setStartDate('');
    setEndDate('');
    setDateFilter('' as DateFilterType);
  };

  useEffect(() => {
    onSelectionChange({
      source: selectedPlatform,
      accounts: selectedAccounts,
      campaigns: selectedCampaigns,
      lineItems: selectedLineItems,
      dateFilter,
      startDate,
      endDate,
    });
    // Intentionally omit onSelectionChange — parent may pass an unstable callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPlatform,
    selectedAccounts,
    selectedCampaigns,
    selectedLineItems,
    dateFilter,
    startDate,
    endDate,
  ]);

  return (
    <div className="flex justify-between w-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div id="platform-dropdown">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={`ml-2 ${className} hover:opacity-65`}
                variant="ghost"
                size="med"
                disabled={isFetchingPlatforms}
              >
                <ChevronDown className="w-4 h-4" />
                {formatPlatformLabel(selectedPlatform)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {platforms.map((platform) => (
                <DropdownMenuItem
                  key={platform}
                  onClick={() => handlePlatformSelection(platform)}
                >
                  {formatPlatformLabel(platform)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div id="account-dropdown">
          {selectedPlatform && (
            <MultiSelector
              isLoading={isFetchingAccounts}
              selected={selectedAccounts}
              items={accounts.map((account) => ({ id: account.id, name: account.name }))}
              onSelectionChange={handleAccountSelection}
              placeholder="Select Accounts"
              label="Accounts"
            />
          )}
        </div>
        <div id="campaign-dropdown">
          {selectedPlatform && selectedAccounts.length > 0 && (
            <MultiSelector
              isLoading={isFetchingCampaigns}
              selected={selectedCampaigns}
              items={campaigns.map((campaign) => ({ id: campaign.id, name: campaign.name }))}
              onSelectionChange={handleCampaignSelection}
              placeholder="Select Campaigns"
              label="Campaigns"
            />
          )}
        </div>
        <div id="line-item-dropdown">
          {selectedPlatform &&
            selectedAccounts.length > 0 &&
            selectedCampaigns.length > 0 && (
              <MultiSelector
                isLoading={isFetchingLineItems}
                selected={selectedLineItems}
                items={lineItems.map((lineItem) => ({ id: lineItem.id, name: lineItem.name }))}
                onSelectionChange={handleLineItemSelection}
                placeholder="Select Line Items"
                label="Line Items"
              />
            )}
        </div>
        <Button
          className="ml-auto bg-gray-201"
          variant="ghost"
          size="med"
          onClick={() => handleClear()}
        >
          <X className="w-4 h-4" />
          Clear
        </Button>
      </div>
      <div className="flex items-center gap-4 border-b border-gray-201" />
      <div className="flex items-center gap-2 justify-end">
        {platforms.length > 0 &&
          selectedAccounts.length > 0 &&
          (startDate === '' || endDate === '') && (
            <div className="flex items-center gap-2 ">
              <ArrowRight className="w-6 h-6 animate-bounce" />
            </div>
          )}
        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'last_week' ? selectedClassName : ''}`}
          variant="primary"
          size="med"
          onClick={() => handleDateChange('last_week')}
        >
          Last week
        </Button>
        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'last_month' ? selectedClassName : ''}`}
          variant="ghost"
          size="med"
          onClick={() => handleDateChange('last_month')}
        >
          Last month
        </Button>
        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'this_month' ? selectedClassName : ''}`}
          variant="ghost"
          size="med"
          onClick={() => handleDateChange('this_month')}
        >
          This month
        </Button>
        <DateRangeModal
          className={className}
          selectedClassName={selectedClassName}
          selectedDate={dateFilter}
          onDateChange={handleDateChange}
        />
      </div>
    </div>
  );
};

export default ReportingApiV1FilterBar;
